import { apiClient } from './apiClient';
import { Test, TestFormData, TestFilters, TestListResponse } from '../types/test';

export const testService = {
  // Obtener todas las pruebas con filtros
  async getTests(filters: TestFilters = {}): Promise<TestListResponse> {
    const params = new URLSearchParams();
    
    if (filters.patientId) params.append('patientId', filters.patientId.toString());
    if (filters.evaluatorId) params.append('evaluatorId', filters.evaluatorId);
    if (filters.testTypeId) params.append('testTypeId', filters.testTypeId.toString());
    if (filters.testTypeCode) params.append('testTypeCode', filters.testTypeCode);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/tests?${params.toString()}`);
    return response.data.data;
  },

  // Obtener prueba por ID
  async getTestById(id: number): Promise<Test> {
    const response = await apiClient.get(`/tests/${id}`);
    return response.data.data;
  },

  // Crear nueva prueba
  async createTest(
    testData: TestFormData,
    imageFiles?: { copia?: File; memoriaInmediata?: File; memoriaDiferida?: File; memoria?: File } | File
  ): Promise<Test> {
    // Si hay imágenes (objeto con copia/memoria o File simple), usar FormData
    if (imageFiles) {
      const formData = new FormData();
      
      // Si es un objeto con copia y memoria (Rey Figure: copia, memoria inmediata, memoria diferida)
      if (typeof imageFiles === 'object' && 'copia' in imageFiles) {
        if (imageFiles.copia) {
          formData.append('imageCopia', imageFiles.copia);
        }
        if (imageFiles.memoriaInmediata) {
          formData.append('imageMemoriaInmediata', imageFiles.memoriaInmediata);
        }
        if (imageFiles.memoriaDiferida) {
          formData.append('imageMemoriaDiferida', imageFiles.memoriaDiferida);
        }
        // Retrocompatibilidad: si viene solo 'memoria', enviar como memoria diferida
        if (imageFiles.memoria && !imageFiles.memoriaDiferida) {
          formData.append('imageMemoriaDiferida', imageFiles.memoria);
        }
      } else if (imageFiles instanceof File) {
        // Si es un File simple (otros tests)
        formData.append('image', imageFiles);
      }
      
      formData.append('testTypeCode', testData.testTypeCode || '');
      formData.append('patientId', testData.patientId.toString());
      formData.append('testData', JSON.stringify(testData.testData));
      formData.append('observations', testData.observations || '');
      
      const response = await apiClient.post('/tests', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.data;
    } else {
      // Sin imagen, enviar JSON normal
      const response = await apiClient.post('/tests', testData);
      return response.data.data;
    }
  },

  // Actualizar prueba
  async updateTest(id: number, testData: Partial<TestFormData>): Promise<Test> {
    const response = await apiClient.put(`/tests/${id}`, testData);
    return response.data.data;
  },

  // Eliminar prueba
  async deleteTest(id: number): Promise<void> {
    await apiClient.delete(`/tests/${id}`);
  },

  // Obtener pruebas de un paciente específico
  async getPatientTests(patientId: number, testTypeCode?: string, testTypeId?: number): Promise<Test[]> {
    const params = new URLSearchParams();
    if (testTypeCode) params.append('testTypeCode', testTypeCode);
    if (testTypeId) params.append('testTypeId', testTypeId.toString());
    
    const response = await apiClient.get(`/tests/patient/${patientId}?${params.toString()}`);
    return response.data.data;
  },

  // Obtener estadísticas de pruebas
  async getTestStatistics(filters: { patientId?: number; testTypeCode?: string; testTypeId?: number } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.patientId) params.append('patientId', filters.patientId.toString());
    if (filters.testTypeCode) params.append('testTypeCode', filters.testTypeCode);
    if (filters.testTypeId) params.append('testTypeId', filters.testTypeId.toString());
    
    const response = await apiClient.get(`/tests/statistics?${params.toString()}`);
    return response.data.data;
  },

  // Calcular percentil y clasificación (preview, no guarda)
  async calculatePercentilePreview(data: {
    testTypeCode: string;
    patientId: number;
    timeSeconds?: number; // Para TMT
    educationYears?: number;
    // Para Stroop
    condition?: string; // 'palabra', 'color', 'palabraColor'
    score?: number | string; // Número de ítems correctos o string para tipo de construcción (Rey Figure)
    // Para Rey Figure
    age?: number; // Edad del paciente
    // Matriz de clasificación a usar: 'original' | 'normal' | 'non-normal'
    matrixType?: string;
  }): Promise<{
    percentile: number | string;
    isRange: boolean;
    classifications: string[];
    // Presentes solo para MoCA (ajuste automático por escolaridad)
    rawScore?: number;
    educationAdjustment?: number;
    adjustedScore?: number;
    // Presentes solo para IFS-Ch: Z-score y la conversión/clasificación en las 3 matrices
    // (original, normal, non-normal), todas referidas al mismo percentil calculado.
    zScore?: number | null;
    matrices?: Record<string, {
      classification: string;
      percentileRange: string | null;
      tScore: { average: number | null };
      standardScore: { average: number | null };
      scaledScore: { average: number | null };
    } | null>;
  }> {
    const response = await apiClient.post('/tests/calculate-percentile', data);
    return response.data.data;
  }
};

